// src/components/PickupAddressModal.jsx
// Shared modal for Add / Edit / Delete pickup (or drop) addresses.
// Used by CreateForwardOrder (Step 2) and CreateReverseOrder (Step 2).
import React, { useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../utils/apiUrls";
import { MapPin, X, Trash2 } from "lucide-react";

const INITIAL = {
  pickup_latitude: "",
  pickup_longitude: "",
  pickup_poc: "",
  pickup_organisation: "",
  pickup_address: "",
  pickup_district: "",
  pickup_city: "",
  pickup_state: "",
  pickup_landmark: "",
  pickup_poc_email: "",
  pickup_poc_contact_dialing_code: "",
  pickup_poc_contact_number: "",
  pickup_postal_code: "",
  pickup_country_code: "IN",
  pickup_address_type: "",
  pickup_hub_code: "",
  gst_number: "",
};

const REQUIRED = [
  "pickup_poc",
  "pickup_organisation",
  "pickup_address",
  "pickup_district",
  "pickup_city",
  "pickup_state",
  "pickup_postal_code",
  "pickup_poc_contact_number",
  "pickup_poc_email",
  "pickup_country_code",
];

/* ── Field config drives the form; SECTIONS group it for reading ── */
const FIELDS = [
  { key: "pickup_poc", label: "Contact person", required: true, placeholder: "Full name" },
  { key: "pickup_organisation", label: "Organisation", required: true, placeholder: "e.g. Baby Teddy" },
  { key: "pickup_address", label: "Street address", required: true, placeholder: "Building, street, area", fullWidth: true, textarea: true },
  { key: "pickup_city", label: "City", required: true, placeholder: "Mumbai" },
  { key: "pickup_district", label: "District", required: true, placeholder: "Mumbai Suburban" },
  { key: "pickup_state", label: "State", required: true, placeholder: "Maharashtra" },
  { key: "pickup_postal_code", label: "Pincode", required: true, placeholder: "400001", maxLength: 6 },
  { key: "pickup_landmark", label: "Landmark", placeholder: "Near City Mall", fullWidth: true },
  { key: "pickup_poc_contact_number", label: "Phone number", required: true, placeholder: "9876543210", maxLength: 10 },
  { key: "pickup_poc_email", label: "Email", required: true, placeholder: "contact@example.com", type: "email" },
  { key: "pickup_country_code", label: "Country", required: true, placeholder: "IN", readOnly: true, fixedValue: "IN" },
  { key: "pickup_poc_contact_dialing_code", label: "Dialing code", placeholder: "+91" },
  { key: "pickup_latitude", label: "Latitude", placeholder: "19.0760", type: "number" },
  { key: "pickup_longitude", label: "Longitude", placeholder: "72.8777", type: "number" },
  { key: "pickup_address_type", label: "Address type", placeholder: "Warehouse" },
  { key: "pickup_hub_code", label: "Hub code", placeholder: "HUB001" },
  { key: "gst_number", label: "GST number", placeholder: "29ABCDE1234F1Z5", uppercase: true },
];

const SECTIONS = [
  { title: "Business", keys: ["pickup_organisation", "pickup_poc"] },
  { title: "Address", keys: ["pickup_address", "pickup_city", "pickup_district", "pickup_state", "pickup_postal_code", "pickup_landmark", "pickup_country_code"] },
  { title: "Contact", keys: ["pickup_poc_contact_dialing_code", "pickup_poc_contact_number", "pickup_poc_email"] },
  { title: "More details", keys: ["pickup_latitude", "pickup_longitude", "pickup_address_type", "pickup_hub_code", "gst_number"] },
];
const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));

const modalInputCls = (hasError, extra = "") =>
  `w-full rounded-xl border bg-white px-3.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-150 ` +
  `focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ` +
  `${hasError ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"} ${extra}`;

/* ═════════════════════════════ Delete Confirm Modal ═════════════════════════════ */
const DeleteConfirmModal = ({ open, onConfirm, onCancel, deleting }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in">
        <div className="px-6 pb-4 pt-5">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </span>
            <h3 className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900">Delete pickup location</h3>
          </div>
          <p className="mt-3 text-[13.5px] leading-relaxed text-slate-500">This removes the saved location for every future order. Orders already booked from it are not affected.</p>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#FAFBFD] px-6 py-4">
          <button onClick={onCancel} disabled={deleting} className="inline-flex h-10 cursor-pointer items-center rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all hover:bg-[#F8FAFD] hover:text-slate-900 disabled:opacity-60">Cancel</button>
          <button onClick={onConfirm} disabled={deleting} className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-xl bg-rose-600 px-4 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(225,29,72,0.5)] transition-all hover:bg-rose-700 active:translate-y-px disabled:opacity-60">
            {deleting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {deleting ? "Deleting…" : "Delete location"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ═════════════════════════════ MAIN EXPORT ═════════════════════════════ */
/**
 * @param {boolean}  open       Whether the modal is visible
 * @param {"add"|"edit"} mode   "add" or "edit"
 * @param {object|null} location Pickup location data when editing
 * @param {function} onClose    Called when the modal should close
 * @param {function} onSaved    Called after a successful add/edit/delete so the parent can refresh
 */
export default function PickupAddressModal({ open, mode = "add", location: loc, onClose, onSaved }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Populate form when opening for edit
  React.useEffect(() => {
    if (open && mode === "edit" && loc) {
      setForm({
        pickup_id: loc.pickup_address_id || loc.pickupId || null,
        pickup_latitude: loc.pickup_latitude ?? "",
        pickup_longitude: loc.pickup_longitude ?? "",
        pickup_poc: loc.pickup_poc ?? "",
        pickup_organisation: loc.pickup_organisation ?? "",
        pickup_address: loc.pickup_address ?? "",
        pickup_district: loc.pickup_district ?? "",
        pickup_city: loc.pickup_city ?? "",
        pickup_state: loc.pickup_state ?? "",
        pickup_landmark: loc.pickup_landmark ?? "",
        pickup_poc_email: loc.pickup_poc_email ?? "",
        pickup_poc_contact_dialing_code: loc.pickup_poc_contact_dialing_code ?? "",
        pickup_poc_contact_number: loc.pickup_poc_contact_number ?? "",
        pickup_postal_code: loc.pickup_postal_code ?? "",
        pickup_country_code: loc.pickup_country_code ?? "IN",
        pickup_address_type: loc.pickup_address_type ?? "",
        pickup_hub_code: loc.pickup_hub_code ?? "",
        gst_number: loc.pickup_gst_number ?? loc.gst_number ?? "",
      });
    } else if (open && mode === "add") {
      // Support pre-filling "add" mode with initial data (e.g. from draft order cloneFrom)
      if (loc) {
        setForm({
          pickup_latitude: loc.pickup_latitude ?? "",
          pickup_longitude: loc.pickup_longitude ?? "",
          pickup_poc: loc.pickup_poc ?? "",
          pickup_organisation: loc.pickup_organisation ?? "",
          pickup_address: loc.pickup_address ?? "",
          pickup_district: loc.pickup_district ?? "",
          pickup_city: loc.pickup_city ?? "",
          pickup_state: loc.pickup_state ?? "",
          pickup_landmark: loc.pickup_landmark ?? "",
          pickup_poc_email: loc.pickup_poc_email ?? "",
          pickup_poc_contact_dialing_code: loc.pickup_poc_contact_dialing_code ?? "",
          pickup_poc_contact_number: loc.pickup_poc_contact_number ?? "",
          pickup_postal_code: loc.pickup_postal_code ?? "",
          pickup_country_code: loc.pickup_country_code ?? "IN",
          pickup_address_type: loc.pickup_address_type ?? "",
          pickup_hub_code: loc.pickup_hub_code ?? "",
          gst_number: loc.pickup_gst_number ?? loc.gst_number ?? "",
        });
      } else {
        setForm({ ...INITIAL });
      }
    }
  }, [open, mode, loc]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    for (const k of REQUIRED) {
      if (!String(form[k] ?? "").trim()) errs[k] = "Required";
    }
    if (!errs.pickup_poc_contact_number && !/^[0-9]{10}$/.test(String(form.pickup_poc_contact_number || "").trim()))
      errs.pickup_poc_contact_number = "Must be 10 digits";
    if (!errs.pickup_postal_code && !/^[0-9]{6}$/.test(String(form.pickup_postal_code || "").trim()))
      errs.pickup_postal_code = "Must be 6 digits";
    if (!errs.pickup_poc_email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(form.pickup_poc_email || "").trim()))
      errs.pickup_poc_email = "Enter a valid email address";
    if (!errs.pickup_address && String(form.pickup_address || "").trim().length < 10)
      errs.pickup_address = "Add the full address — at least 10 characters";
    if (Object.values(errs).some(Boolean)) {
      setFieldErrors(errs);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setFieldErrors({});
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (mode === "add") delete payload.pickup_id;
      await axios.post(API.addOrUpdatePickupDetails(merchantId), [payload], {
        headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" },
      });
      toast.success(mode === "edit" ? "Pickup location updated!" : "Pickup location added!");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(mode === "edit" ? "Failed to update pickup." : "Failed to add pickup.");
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-2 backdrop-blur-sm animate-fade-in sm:p-4" onClick={onClose}>
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 pb-4 pt-5">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_6px_14px_-4px_rgba(56,119,252,0.4)]">
              <MapPin className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900">{mode === "edit" ? "Edit pickup location" : "Add pickup location"}</h2>
              <p className="mt-0.5 text-[12.5px] text-slate-500">The address the courier collects from.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form body */}
        <div className="custom-scrollbar max-h-[calc(92vh-176px)] overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} id="pickup-addr-form" noValidate>
            {SECTIONS.map((section, sIdx) => (
              <div key={section.title} className={sIdx > 0 ? "mt-6" : ""}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{section.title}</p>
                <div className="mt-2.5 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                  {section.keys.map((key) => {
                    const f = FIELD_BY_KEY[key];
                    return (
                      <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""}>
                        <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
                          {f.label}
                          {!f.required && <span className="font-medium text-slate-400"> · optional</span>}
                        </label>
                        {f.textarea ? (
                          <textarea
                            name={f.key}
                            value={form[f.key]}
                            onChange={handleChange}
                            rows={2}
                            className={modalInputCls(fieldErrors[f.key], "resize-none py-2.5")}
                            placeholder={f.placeholder}
                          />
                        ) : f.readOnly ? (
                          <input
                            type="text"
                            name={f.key}
                            value={f.fixedValue}
                            readOnly
                            className="h-11 w-full cursor-not-allowed rounded-xl border border-[#E4E9F1] bg-[#F8FAFC] px-3.5 text-sm text-slate-500"
                          />
                        ) : (
                          <input
                            type={f.type || "text"}
                            step={f.type === "number" ? "any" : undefined}
                            name={f.key}
                            value={form[f.key]}
                            onChange={handleChange}
                            maxLength={f.maxLength}
                            className={modalInputCls(fieldErrors[f.key], `h-11 ${f.uppercase ? "uppercase tracking-wider" : ""}`)}
                            placeholder={f.placeholder}
                          />
                        )}
                        {fieldErrors[f.key] ? <p className="mt-1.5 text-[12px] font-semibold text-rose-600">{fieldErrors[f.key]}</p> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#FAFBFD] px-6 py-4">
          <button type="button" onClick={onClose} className="inline-flex h-11 cursor-pointer items-center rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all hover:bg-[#F8FAFD] hover:text-slate-900 active:scale-[0.98]">
            Cancel
          </button>
          <button type="submit" form="pickup-addr-form" disabled={submitting} className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-xl bg-[#3877FC] px-5 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:opacity-60 disabled:shadow-none">
            {submitting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {submitting ? (mode === "edit" ? "Saving…" : "Adding…") : mode === "edit" ? "Save changes" : "Add location"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Export the delete handler as a standalone utility ── */
export async function deletePickupAddress(pickupAddressId, onDone) {
  const merchantId = localStorage.getItem("merchantId");
  const authToken = localStorage.getItem("authToken");
  try {
    await axios.delete(API.deletePickupDetails(merchantId, pickupAddressId), {
      headers: { Authorization: authToken, "x-merchant-id": merchantId },
    });
    toast.success("Pickup location deleted!");
    onDone?.();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete pickup location.");
  }
}

/* ── Also export the DeleteConfirmModal for inline usage ── */
export { DeleteConfirmModal };
